import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { FileDown, Loader2, ShieldCheck, Check } from "lucide-react";
import { openSnapshot } from "@/lib/riskQuiz";
import { inputCls, labelCls } from "@/components/site/SectionHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SnapshotForm = ({ result }) => {
  const [form, setForm] = useState({ name: "", email: "", consent: false });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return setError("Please enter your name and email.");
    if (!form.consent) return setError("Please confirm consent to continue.");
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/consultations`, {
        type: "risk_snapshot",
        name: form.name,
        email: form.email,
        consent: true,
        risk_score: result.score,
        risk_level: result.level.label,
        risk_areas: result.gaps.map((g) => g.area),
        message: `Risk Readiness Score ${result.score}/100 (${result.level.label}).`,
      });
      setStatus("success");
      openSnapshot(form.name, result);
    } catch (_) {
      setStatus("idle");
      setError("Something went wrong. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div className="mt-6 rounded-lg border border-emerald-400/30 bg-emerald-500/5 p-4" data-testid="snapshot-success">
        <div className="flex items-center gap-2 text-emerald-400 text-sm font-body">
          <Check className="w-4 h-4" /> Your personalized snapshot is ready.
        </div>
        <button onClick={() => openSnapshot(form.name, result)} className="btr-btn-ghost mt-4 w-full justify-center" data-testid="snapshot-download-btn">
          <FileDown className="w-4 h-4" /> Download / Print Snapshot
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-6 space-y-3 btr-page-enter" data-testid="snapshot-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Name *</label>
          <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" data-testid="snapshot-name" />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@firm.com" data-testid="snapshot-email" />
        </div>
      </div>
      <label className="flex items-start gap-3 text-xs text-zinc-400 font-body leading-relaxed cursor-pointer">
        <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 accent-[#a2020b]" data-testid="snapshot-consent" />
        I agree that Before The Risk may store my answers and contact me about my results. No spam, unsubscribe anytime.
      </label>
      {error && <div className="text-[#ff6b72] text-xs font-body" data-testid="snapshot-error">{error}</div>}
      <button type="submit" disabled={status === "loading"} className="btr-btn-primary w-full justify-center" data-testid="snapshot-submit">
        {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Send My Snapshot
      </button>
    </form>
  );
};

export const RiskResult = ({ result }) => {
  const [wantSnapshot, setWantSnapshot] = useState(false);
  return (
    <div className="btr-page-enter" data-testid="risk-result">
      <div className="text-[11px] font-display tracking-[0.25em] uppercase text-zinc-500">Your Preliminary Score</div>
      <div className="flex items-end gap-3 mt-3">
        <span className="font-display font-extrabold text-white text-6xl sm:text-7xl leading-none" data-testid="risk-score-value">{result.score}</span>
        <span className="text-zinc-500 font-body mb-2">/ 100</span>
      </div>
      <div className={`font-display font-bold text-lg mt-3 ${result.level.tone}`} data-testid="risk-level">{result.level.label}</div>
      <p className="font-body text-zinc-400 text-sm mt-2 leading-relaxed">{result.level.note}</p>
      <div className="h-1.5 bg-white/5 rounded-full mt-5 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#a2020b] to-[#ff4d55] transition-[width] duration-700" style={{ width: `${result.score}%` }} />
      </div>
      {result.gaps.length > 0 && (
        <div className="mt-7" data-testid="risk-gaps">
          <div className="text-[11px] font-display tracking-[0.25em] uppercase text-zinc-500">Identified Risk Areas</div>
          <ul className="mt-3 space-y-2">
            {result.gaps.map((g) => (
              <li key={g.id} className="flex gap-3 text-sm font-body text-zinc-300">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#a2020b] flex-shrink-0" /> {g.area}
              </li>
            ))}
          </ul>
        </div>
      )}
      {wantSnapshot ? (
        <SnapshotForm result={result} />
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <button onClick={() => setWantSnapshot(true)} className="btr-btn-primary btr-cta-breathe justify-center" data-testid="get-snapshot-btn">
            <FileDown className="w-4 h-4" /> Get My Risk Snapshot
          </button>
          <Link to="/contact" className="btr-btn-ghost justify-center" data-testid="risk-consult-btn">Schedule Consultation</Link>
        </div>
      )}
      <p className="text-[11px] text-zinc-600 font-body mt-5">Preliminary and educational only — based on your answers, not an inspection or legal advice.</p>
    </div>
  );
};
